import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { genTinyGUID, SawDust } from '@cudirect/shared-ui-library';
import { BuildingBlock, BuildingBlockControlBaseComponent } from '../building-block.types';
import { ApiResult, ApiErrorResult } from '@workflow/ecp-workflow/dynamic-app/service/application-api/application-api.types';
import { InstaTouchUserInformation } from '@workflow/ecp-workflow/dynamic-app/service/instaTouch/insta-touch.types';
import { InstaTouchState, InstaTouchSession } from '@shared/model/instatouch.model';
import { LoanType } from '@shared/enum';
import { FlowType } from '@workflow/ecp-shared/model/types';
import { UserInfo } from '@workflow/ecp-shared/model/userinfo';
import { ConsumerLogin } from 'src/user/model/consumer-login';
import { OIDLoginWidgetExportData } from '@cudirect-widgets/ea-login';

interface GetLoginDataResponse {
  oldSession: string;
  user: ConsumerLogin;
  prevSession: string;
  comingFromHeavyReg: string;
}

interface GetLoginDataProps {
  email: string;
  phoneNumber: string;
  accessToken: string;
  refreshToken: string;
  ssoLogin?: boolean;
  idToken?: string;
  expiresIn?: string;
  authCode?: string;
  extToken?: string;
  discoveryURL?: string;
  loginMode: 'login' | 'heavyReg' | 'verify';
}

interface HandleReceivedDataForLoginModeResponse {
  phoneNumber?: string;
  email?: string;
}

/** this component helps to retrive personal information using mobile number */
@BuildingBlock('personal-info-retrieval')
@Component({
  selector: 'ecp-autofill-application',
  templateUrl: './autofill-application.component.html',
  standalone: false
})
export class AutofillApplicationComponent extends BuildingBlockControlBaseComponent {
  protected logger = inject(SawDust).withContext({ component: 'AutofillApplicationComponent' });

  /** Widget Export Data */
  public exportData: InstaTouchUserInformation;

  /** currentStage */
  public currentStage: string;

  /** flag to indicate Api call in progress */
  public isLoading: boolean;

  /** Instatouch Session */
  public instaTouchSessionId: string;

  /** Instatouch error message */
  public errorMessage: string;

  /** instaTouch stage */
  public stage: InstaTouchState = 'initial';

  /** mobileNumber */
  private mobileNumber: string;
  public showCodeSentAlert = false;

  public get flowType(): FlowType {
    if (!this.userService?.flowType) {
      this.logger.info('flowType is not set in autofill application component');
    }
    return this.userService?.flowType;
  }

  /** Hold the value to Show and hide OAO Flow texts. */
  public get isOaoFlow() {
    return this.flowType.toLowerCase() === LoanType.OAO.toLowerCase();
  }
  /** Hold the value to Show and hide OLA Flow texts. */
  public get isOlaFlow() {
    return this.flowType.toLowerCase().toLowerCase() === LoanType.OLA.toLowerCase();
  }

  /** Passcode */
  public getPassCode({ mobileNumber }: InstaTouchSession): void {
    this.isLoading = true;
    this.errorMessage = null;
    if (this.preCheck(mobileNumber)) {
      // if(this.field.data['backend']['data']['api:borrowerInfo/primaryPhoneNumber'] === event.mobileNumber){
      this.isLoading = false;
      this.errorMessage = "You can't use the same number for multiple applicants. Please use a different number.";
    } else {
      this.instaTouchService.generatePasscode(mobileNumber ? mobileNumber.toString() : '').subscribe(
        (response) => {
          // this.field.data.value = event.mobileNumber;

          this.mobileNumber = mobileNumber;

          // autofill enabled for multiple applicants, so its not require here, otherwise overrides value
          // else {
          //     this.statefulService.wrap(`api:oaoMemberInformation/primaryApplicant/mobileNumber`).value = event.mobileNumber;
          // }

          if (response.isSuccess) {
            if (this.stage === 'passcode') this.showCodeSentAlert = true;
            this.statefulService.wrap('ss:oao-primary-applicant-prefill-alert-success').value = null;
            this.statefulService.wrap('ss:oao-primary-co-applicant-prefill-alert-success').value = null;
            this.statefulService.wrap('ss:oao-primary-additional-co-applicant-prefill-alert-success').value = null;
            this.instaTouchSessionId = response.data.AccessToken;
            this.stage = 'passcode';
            this.isLoading = false;
          }
        },
        (err: HttpErrorResponse) => {
          this.errorHandler(err, 'personalInfoAutofill/errorGeneratingPasscode');
        }
      );
    }
  }
  /** Validate Passcode */
  public validatePassCode({ passCode }: InstaTouchSession): void {
    this.isLoading = true;
    this.errorMessage = null;
    this.showCodeSentAlert = false;
    this.instaTouchService.validatePasscode(this.instaTouchSessionId, passCode ? passCode.toString() : '', '').subscribe({
      next: (response: ApiResult<boolean>) => {
        if (response.data && response.isSuccess) {
          this.stage = 'identity';
          this.isLoading = false;
        }
      },
      error: (err: HttpErrorResponse) => {
        this.errorHandler(err, 'personalInfoAutofill/errorValidatingPasscode');
      }
    });
  }

  /**
   * Initializes the token service with configuration settings and sets up caching for user session tokens.
   *
   * @remarks
   * This method is used to initialize the token service with necessary configuration and API keys.
   * It also configures the caching mechanism for storing user session tokens in either `sessionStorage`
   * or another specified storage type.
   *
   * @returns A promise that resolves once the token service is successfully initialized and configured.
   */
  private async setupTokenInitialize(): Promise<void> {
    return this.tokenService
      .initialize(
        this.configService?.settings?.oid.apiBaseURL,
        this.xApiKeyService.createApiHeader(),
        this.configService?.settings?.oid.oidConfigName,
        true
      )
      .then(() => {
        this.tokenService.configureCache(this.configService?.settings?.arcDXTokenStorageType || 'sessionStorage', 'ecp-user-session');
      });
  }

  private async setUpVerifyFlow({
    accessToken,
    refreshToken
  }: {
    accessToken: string;
    refreshToken: string;
  }): Promise<OIDLoginWidgetExportData> {
    // This function is named the same as that found in login component.
    // Original parameters: none

    // this grabbed the user from userService that we create in this
    //   flow, but we aren't going to do it now since we have one
    //   created now from the mfa skip login flow
    // const userInfo = this.userService.user;
    // this.userId = phonenumber; // only used to track heavy reg in login component
    // this was also only used to track heavy reg in login component,
    //   but the userInfo.mfaVerifyMethod should equal to 'Phone'.
    // this.verifyMethod = 'Phone';

    // instead we are going to create the data object that is passed to the next
    // function, handleReceivedDataForLoginMode
    const data = {
      loginRedirect: false,
      provider: '',
      statusMsg: 'Authenticated',
      token: {
        access_token: accessToken,
        refresh_token: refreshToken,
        // these aren't necessary in this case
        expires_in: 0,
        id_token: null,
        token_type: null
      }
    } as OIDLoginWidgetExportData;

    return Promise.resolve(data);
  }

  private async receivedDataHandler(data: OIDLoginWidgetExportData): Promise<OIDLoginWidgetExportData> {
    // This function is named the same as that found in login component.
    // Original parameters: (data: OIDLoginWidgetExportData)

    // The data parameter was retrieved form the loginService.getLoginData()
    // it functioned like a gatekeeper for the login process, depending on the login mode.
    return Promise.resolve(data);
  }

  private async handleReceivedDataForLoginMode(data: OIDLoginWidgetExportData): Promise<HandleReceivedDataForLoginModeResponse> {
    let accessToken: string;

    if (typeof data.token === 'string') {
      accessToken = data.token;
    } else if (data.token && 'access_token' in data.token) {
      accessToken = data.token.access_token;
    } else {
      throw new Error('Invalid token format');
    }

    const decodedToken = this.tokenService.decodeToken(accessToken);
    const { email, phonenumber } = decodedToken;

    return Promise.resolve({
      phoneNumber: phonenumber,
      email
    });
  }

  private async getLoginData({
    email = '',
    phoneNumber = '',
    accessToken = '',
    refreshToken = '',
    ssoLogin = false,
    loginMode
  }: GetLoginDataProps): Promise<GetLoginDataResponse> {
    const user = {} as ConsumerLogin;
    // Widget Login Flow
    user.email = email;
    user.accessToken = accessToken;
    user.refreshToken = refreshToken;
    user.loginWithSSO = ssoLogin;
    user.cellPhone = phoneNumber;

    const oldSession = this.statefulService.wrap(`${this.isOlaFlow ? 'ss:auth-ola' : 'ss:authorization'}`).valueString; // guid
    let prevSession = '';
    if (loginMode === 'verify') {
      prevSession = oldSession ?? null; // prevSession is same guid as oldSession
    }
    this.statefulService.wrap('ss:loginType').value = false;
    const comingFromHeavyReg = sessionStorage.getItem('arcDXHeavyReg'); // is null

    // before the function would call getLoginDataContinued(), but we are going to pivot away from that now
    return Promise.resolve({
      oldSession,
      user,
      prevSession,
      comingFromHeavyReg
    });
  }

  private async getLoginDataContinued({ oldSession }: { oldSession: string }): Promise<void> {
    sessionStorage.removeItem('arcDXHeavyReg');
    if (this.userSession.canHookCurrentBundle && oldSession) {
      this.userService.user = new UserInfo();
      this.userService.user.token = oldSession;
    }
    // skip call consumerUserService.login() here because the user is already created
    // user and prevSession were used with consumerUserService.login(), so they were removed as parameters
    // we will not have a response from consumerUserService.login() either.
    this.userSession.canHookCurrentBundle = false;
    // 6. SetUser(response.data) // originally setUser called from this method,....
    // setUser(response.data)

    // one of these two routes below takes us to step 7. afterLogin
    // 7. afterLogin(response)
    // const loanLinkInfo = this.GetLoanLinkInfo(user);
    // if (!loanLinkInfo.loanNumber || !loanLinkInfo.borrowerId) {
    //   this.afterLogin(response);
    //   return;
    // }
    // this.consumerUserService.LinkLoan(loanLinkInfo).subscribe((): void => {
    //   this.afterLogin(response);
    // });
    return Promise.resolve();
  }

  private async setUser({
    user,
    oldSession,
    loginType
  }: {
    user: ConsumerLogin;
    oldSession: string;
    loginType?: string;
  }): Promise<{ userInfo: UserInfo }> {
    // originally was called with a parameter of reponse: LoginResponse
    const userInfo = new UserInfo();
    userInfo.email = user.email;
    userInfo.cellPhone = user.cellPhone;
    userInfo.isCellPhoneUser = !!user.cellPhone;
    // userInfo.mfaVerifyMethod should equal to 'Phone'.
    userInfo.mfaVerifyMethod = 'Phone';
    // prior was: response.credentials.access_token
    // updated would be: user.accessToken
    // however, we set it to oldSession, the guid from the previous session
    userInfo.token = oldSession;
    userInfo.token_time = new Date();
    userInfo.confetti = [];
    userInfo.registeredWithSSO = user.loginWithSSO; // response.applicationBundle.registeredWithSSO;

    // this is not in the original SetUser(), but assuming it comes back from the call
    userInfo.loginType = loginType;

    this.userService.user = userInfo;
    if (loginType?.toLowerCase() === LoanType.OLA.toLowerCase()) {
      this.userService.flowType = LoanType.OLA;
    } else if (loginType?.toLowerCase() === LoanType.OAO.toLowerCase()) {
      this.userService.flowType = LoanType.OAO;
    }

    this.userSession.startTimer();
    // prior was response.credentials.access_token
    // however, we set it to oldSession, the guid from the previous session
    this.statefulService.wrap('ss:authorization').value = oldSession;
    return Promise.resolve({ userInfo });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private async afterLogin({ userInfo, loginMode }: { userInfo: UserInfo; loginMode: 'login' | 'heavyReg' | 'verify' }): Promise<void> {
    // Originall this was called with parameter of response: Result<LoginResponse>
    if (this.userService.user?.loginType && this.userService.user.loginType.toLowerCase() === 'oao') {
      this.l360Service.setOAOLenderSettings();
    }

    // TOOD: I'm pretty sure we dont want to navigate here, perhaps elsewhere
    // if (loginMode === 'verify') {
    //   this.router.navigate([RoutingUrl.OAO.Overview]);
    // } else {
    //   this.router.navigate([RoutingUrl.Dashboard]);
    // }

    return Promise.resolve();
  }

  private async setOAOMember({ userInfo }: { userInfo: UserInfo }): Promise<void> {
    if (userInfo) {
      this.pageNavigationStateService.setStoragePrefix('oao');
      this.pageNavigationStateService.retrievePageState();
    }
    return Promise.resolve();
  }

  /** Personal Info */
  public getPersonalInfo({ ssn, zipCode, isPrimaryApplicant = false }: InstaTouchSession): void {
    const eventPairId = genTinyGUID(24); // TODO: Replace this with cypto.randomUUID() when TS is updated
    this.isLoading = true;
    this.errorMessage = null;
    this.logEvent('start', eventPairId);
    this.instaTouchService
      .getPersonalInformation({
        sessionId: this.instaTouchSessionId,
        zipCode: zipCode ? zipCode.toString() : '',
        last4ssn: ssn ? ssn.toString() : '',
        requestId: '',
        isPrimaryBorrower: isPrimaryApplicant,
        mobileNumber: this.mobileNumber
      })
      .subscribe({
        next: (response: ApiResult<InstaTouchUserInformation>) => {
          if (response?.isSuccess && response?.data && (response.data.personalInfo || response.data.employmentInfo)) {
            /* Converting instatouch response to JSON string and setting it as field data.*/
            const data = response.data;

            if (data.personalInfo.identity.contact.mobile && this.field.data.valueString) {
              data.personalInfo.identity.contact.mobile = this.field.data.valueString;
            }

            let promise = Promise.resolve();
            // Adding check to make sure user is not already logged in.
            if (data?.internalJwtToken?.access_token && data?.internalJwtToken?.refresh_token && !this.userService.isAuthenticated) {
              // Controls whether to show the user dropdown.
              this.userService.isExistingUser = data.isExistingUser;

              // I have the internalJwtToken, now send it to login component
              // Retrieve loginType from StatefulValue
              const loginType = this.statefulService.wrap('api:loginType').value as string;

              const { access_token: accessToken, refresh_token: refreshToken } = data.internalJwtToken;
              this.tokenService.updateToken({
                access_token: accessToken,
                refresh_token: refreshToken,
                expires_in: 0,
                id_token: null,
                token_type: null
              });

              // duplicate logic from login component
              const loginMode = 'verify';
              promise = this.setupTokenInitialize()
                .then(() => {
                  return this.setUpVerifyFlow({
                    refreshToken,
                    accessToken
                  });
                })
                .then((result: OIDLoginWidgetExportData) => {
                  return this.receivedDataHandler(result);
                })
                .then((result: OIDLoginWidgetExportData) => {
                  return this.handleReceivedDataForLoginMode(result)
                    .then((handled: HandleReceivedDataForLoginModeResponse) => {
                      return this.getLoginData({
                        loginMode,
                        email: handled.email,
                        phoneNumber: handled.phoneNumber,
                        accessToken,
                        refreshToken,
                        ssoLogin: false
                      });
                    })
                    .then((loginData: GetLoginDataResponse) => {
                      // eslint-disable-next-line @typescript-eslint/no-unused-vars
                      const { oldSession, user, comingFromHeavyReg } = loginData;
                      let ipromise;
                      // prevSession used to be used in getLoginDataContinued() when it called this.userService.login()
                      // isAllBackendInitialized() is false through the oao flow, compared to ola flow
                      if (oldSession && !comingFromHeavyReg && this.storageService.isAllBackendInitialized()) {
                        ipromise = this.getLoginDataContinued({ oldSession });
                      } else {
                        ipromise = this.storageService.initAllBackends().then(() => {
                          return this.getLoginDataContinued({ oldSession });
                        });
                      }
                      return ipromise
                        .then(() => {
                          return this.setUser({ user, oldSession, loginType });
                        })
                        .then(({ userInfo }: { userInfo: UserInfo }) => {
                          return this.afterLogin({ userInfo, loginMode }).then(() => {
                            if (this.userService.user?.loginType && this.userService.user.loginType.toLowerCase() === 'oao') {
                              return this.setOAOMember({ userInfo });
                            }
                          });
                        });
                    });
                })
                .then(() => {})
                .catch((error: HttpErrorResponse) => {
                  this.errorHandler(error, 'getPersonalInfo/instatouch');
                });
            }

            promise.then(() => {
              data.givenMobileNumber = this.mobileNumber;
              this.field.data.value = JSON.stringify(data);
              /* Need to be refactored */
              /* To update data, Temporaryly binding update method to click event */
              this.onClick(new Event('click'));
              this.stage = 'completed';
              this.isLoading = false;
              this.logEvent('end', eventPairId, 'success');
            });
          } else {
            this.logEvent('end', eventPairId, 'error');
            this.errorHandler({} as HttpErrorResponse, 'personalInfoAutofill/errorValidatingPersonalInfo');
          }
        },
        error: (err: HttpErrorResponse) => {
          this.errorHandler(err, 'personalInfoAutofill/errorValidatingPersonalInfo');
          this.logEvent('end', eventPairId, 'error');
        }
      });
  }

  public errorHandler(err: HttpErrorResponse, errorMessagePath: string): void {
    // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
    const errorResponse: ApiErrorResult | undefined = err?.error;
    this.errorMessage =
      errorResponse?.userMessage === undefined || errorResponse?.statusCode === '500'
        ? this.dictionary.read(errorMessagePath)
        : errorResponse.userMessage;
    this.isLoading = false;
  }

  private preCheck(mobileNumber): boolean {
    const validations = this.field.config.validationCondition;
    const preCheckFn = validations && validations.preCheck;
    if (preCheckFn) {
      return this.logicService.evaluate(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [...preCheckFn, mobileNumber] as any,
        { field: this.field },
        true,
        this.field.iterationId,
        this.field.pageIterationId
      );
    }
    return false;
  }

  private logEvent(eventState: 'start' | 'end', correlationId: string, status?: 'success' | 'error'): void {
    this.applicationInsightsService.logEvent('ecp.ui.instaTouchPrefill', {
      eventState,
      correlationId,
      status
    });
  }

  /** Capture the event from insta-touch card when user click manual flow link.  */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public proceedWithoutPhonePrefill(event): void {
    this.field.data.value = JSON.stringify({ isManual: true });
    this.onClick(new Event('click'));
  }
}
